import { LabeledCheckbox } from '@crio/crio-react-component/dist/cjs/components/Inputs/CrioCheckboxGroup';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { CircularProgress, ThemeProvider } from '@mui/material';
import CrioButton from '@crio/crio-react-component/dist/cjs/components/Inputs/CrioButton';
import crioTheme from '@crio/crio-react-component/dist/cjs/config/crioTheme';
import { Column } from '../index';
import { deleteAllVisitLocks } from '../../api/esourceService';
import { removeAllProcedureLockCookies } from '../../api/cookies';

const StyledReleaseLocksPage = styled(Column)`
  h2 {
    margin: 0 0 20px 0;
    font-size: 1.5em;
    font-weight: 500;
    color: ${(props) => props.theme.palette.grey[900]};
  }

  .WarningBox {
    background-color: ${(props) => props.theme.palette.grey.A100};
    border: 1px solid ${(props) => props.theme.palette.grey[300]};
    color: ${(props) => props.theme.palette.grey[700]};
    padding: 16px;
    line-height: 1.6em;
    font-size: 0.95em;
    margin-bottom: 20px;

    strong {
      font-weight: 600;
    }

    ul {
      margin: 4px 0 0 20px;
      padding: 0;

      li {
        padding: 2px 0;
      }
    }
  }

  .ConfirmCheckbox {
    margin-bottom: 16px;

    .MuiTypography-root.MuiFormControlLabel-label {
      font-size: 1em;
    }
  }

  .SuccessMessage {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 4px;
    color: ${(props) => props.theme.palette.success.dark};
    font-size: 0.95em;
    font-weight: 400;
  }
`;

interface ReleaseLocksPageProps {
  readOnly: boolean;
}

export default function ReleaseLocksPage({ readOnly }: ReleaseLocksPageProps) {
  const { studyId, subjectId, visitId } = useParams();
  const { t } = useTranslation();
  const [confirmed, setConfirmed] = useState<boolean>(false);
  const [isUnlocking, setIsUnlocking] = useState<boolean>(false);
  const [unlockComplete, setUnlockComplete] = useState<boolean>(false);

  const handleReleaseLocks = async () => {
    if (studyId && subjectId && visitId) {
      setIsUnlocking(true);
      try {
        removeAllProcedureLockCookies();
        await deleteAllVisitLocks({ studyId, subjectId, visitId });
        setUnlockComplete(true);
      } finally {
        setIsUnlocking(false);
      }
    }
  };

  return (
    <ThemeProvider theme={crioTheme}>
      <StyledReleaseLocksPage className="CenterPageBackground CenterPagePadding">
        <h2>{t('Visit.Release Locks.Header')}</h2>
        <div className="WarningBox">
          {t('Visit.Release Locks.Warning')}
          <br />
          <br />
          <strong>{t('Visit.Release Locks.Before You Continue')}</strong>
          <ul>
            <li>{t('Visit.Release Locks.Close Tabs')}</li>
            <li>{t('Visit.Release Locks.Confirm Team')}</li>
          </ul>
        </div>
        {!readOnly && (
          <>
            <div className="ConfirmCheckbox">
              <LabeledCheckbox
                disabled={isUnlocking || unlockComplete}
                label={t('Visit.Release Locks.Confirm Checkbox')}
                name="confirm-unlock"
                checked={confirmed}
                onChange={() => setConfirmed(!confirmed)}
              />
            </div>
            <div>
              <CrioButton
                disabled={!confirmed || isUnlocking || unlockComplete}
                onClick={handleReleaseLocks}
              >
                {isUnlocking
                  ? <CircularProgress size={20} color="inherit" />
                  : t('Visit.Release Locks.Unlock Button')}
              </CrioButton>
              {unlockComplete && (
              <div className="SuccessMessage">
                <FontAwesomeIcon icon={faCheckCircle as IconProp} />
                {t('Visit.Release Locks.Success')}
              </div>
              )}
            </div>
          </>
        )}
      </StyledReleaseLocksPage>
    </ThemeProvider>
  );
}
